import { PopoverPlacement } from "@idui/react-popover";
import { PhoneForwarded as PhoneForwardedIcon } from "@mui/icons-material";
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { SVG_MUI } from "@ryde/assets";
import { usePageContext } from "@ryde/redux";
import {
  callToClickHandler,
  DrawerType,
  ENTITY_SEND_MESSAGE_VIA_PHONE_CELL_CONFIRM_LIST,
  formatPhoneLocal,
  useDrawers,
  type IExtendableDto,
  type IStyledAttributes,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { AccessControl, FramerTooltip } from "../../common";
import { useDecorateWithMark } from "../../utils";
import type { IRenderColumnTemplate } from "./_core";

const PhoneRowStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "phone-row-container",
})`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: 100%;

  .phone_forwarded_icon,
  .phone-row__message-icon {
    flex-shrink: 0;
  }

  .phone_forwarded_icon {
    font-size: 13px;
    display: flex;
    align-items: center;
    color: ${rydeColors.steel};
    cursor: pointer;
  }

  .phone-row__value-wrap {
    display: flex;
    min-width: 0;
    flex: 1;
    overflow: hidden;

    > * {
      display: block;
      min-width: 0;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
    }
  }

  .phone-row__value {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PhoneCellTemplate: FC<IRenderColumnTemplate> = ({
  id,
  value,
  name,
  formFieldTypeId,
  e2eData,
  rowOriginal,
}) => {
  const rowData = rowOriginal as IExtendableDto;
  const { openDrawer } = useDrawers();
  const { decorateValue } = useDecorateWithMark();
  const { entity } = usePageContext();
  const { t } = useTranslation();

  const localPhone = formatPhoneLocal(value ? String(value) : "");

  const displayValue = useMemo(() => {
    return decorateValue(localPhone);
  }, [localPhone, decorateValue]);

  const handleSendMessageClick = (): void => {
    if (rowData.id) {
      if (entity === "driver") {
        openDrawer({
          drawerProps: {
            type: DrawerType.BulkDriversMessage,
            selectedDriverIds: [rowData.id],
          },
        });
      } else {
        openDrawer({
          drawerProps: {
            type: DrawerType.BulkPassengersMessage,
            selectedPassengersIds: [rowData.id],
          },
        });
      }
    }
  };

  return (
    <div
      className="cell_simple"
      data-e2e-id={`${name}_fieldid_${formFieldTypeId}`}
      data-e2e-value={value}
      data-e2e-template="phone-cell"
      data-e2e-custom={e2eData}
      data-e2e-custom-field-id={id}
    >
      <PhoneRowStyled>
        {value && (
          <FramerTooltip
            content={`${t("general.PHONE_CALL")} ${localPhone}`}
            placement={PopoverPlacement.bottom}
          >
            <PhoneForwardedIcon
              className="phone_forwarded_icon"
              onClick={() => callToClickHandler(value)}
            />
          </FramerTooltip>
        )}

        {value &&
          ENTITY_SEND_MESSAGE_VIA_PHONE_CELL_CONFIRM_LIST.includes(entity) &&
          (entity === "passenger" ? (
            <AccessControl entity="passenger" action="send_message">
              <FramerTooltip
                content={`${t("general.SEND_MESSAGES")}`}
                placement={PopoverPlacement.bottom}
              >
                <SVG_MUI.MessageEmpty3SVG
                  className="pointer phone-row__message-icon"
                  onClick={handleSendMessageClick}
                />
              </FramerTooltip>
            </AccessControl>
          ) : (
            <FramerTooltip
              content={`${t("general.SEND_MESSAGES")}`}
              placement={PopoverPlacement.bottom}
            >
              <SVG_MUI.MessageEmpty3SVG
                className="pointer phone-row__message-icon"
                onClick={handleSendMessageClick}
              />
            </FramerTooltip>
          ))}

        <div className="phone-row__value-wrap">
          <FramerTooltip
            content={localPhone}
            placement={PopoverPlacement.bottom}
          >
            <span className="phone-row__value truncate-text">
              {displayValue}
            </span>
          </FramerTooltip>
        </div>
      </PhoneRowStyled>
    </div>
  );
};
